import FlexItem, { FlexItemProps } from "@insite/content-library/Widgets/Brand/FlexItem";
import FlexWrapContainer, { FlexWrapContainerProps } from "@insite/content-library/Widgets/Brand/FlexWrapContainer";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { LinkProps } from "@insite/mobius/Link";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { css } from "styled-components";

interface DivisionModel {
    fields: {
        title: string;
        background: "image";
        image: string;
        destination: LinkFieldValue;
    };
}

const enum fields {
    divisions = "divisions",
    numberOfColumns = "numberOfColumns",
    isSpecialPage = "isSpecialPage",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.divisions]: DivisionModel[];
        numberOfColumns: number;
        isSpecialPage: boolean;
    };
    extendedStyles?: PdfStyles;
}

export interface PdfStyles {
    divisionWrapper?: InjectableCss;
    container?: InjectableCss;
    divisionContainer?: FlexWrapContainerProps;
    divisionItem?: FlexItemProps;
    link?: LinkProps;
    image?: LazyImageProps;
    carouselSlideInner?: InjectableCss;
    titleText?: TypographyProps;
    divisionTitle: TypographyProps;
}

export const pdfStyles: PdfStyles = {
    divisionWrapper: {
        css: css`
            padding: 17.5px 0;
            position: relative;
            max-width: 1440px;
            margin: 0 auto;
            margin-top: 0px;
            padding-top: 0;
            margin-bottom: 20px;
            &.specialPage {
                img {
                    width: 85%;
                }
                div[class^="FlexWrapContainer"] {
                    justify-content: center;
                    div[class^="FlexItem"] {
                        margin-bottom: 15px;
                    }
                    h3 {
                        color: #676767;
                        font-weight: normal;
                        font-size: 18px;
                    }
                }
            }
        `,
    },
    titleText: {
        variant: "h3",
        transform: "uppercase",
        css: css`
            border-width: 20px;
            text-align: center;
            margin: 0;
            padding: 30px 0;
            font-size: 16px !important;
        `,
    },
    divisionContainer: {
        css: css`
            justify-content: flex-start;
            > div[class^="GridWrapper"] {
                > div[class^="GridOffset"] {
                    max-width: 100%;
                }
            }
            & .divisionContent {
                width: 100%;
            }
        `,
    },
    carouselSlideInner: {
        css: css`
            position: relative;
        `,
    },
    link: {
        css: css`
            display: block;
            position: relative;
            & div[class^="LazyImageStyle"] {
                width: 100%;
            }
            &:hover h3 {
                text-decoration: underline;
            }
        `,
    },
    divisionTitle: {
        variant: "h6",
        as: "h3",
        css: css`
            @media print {
                font-size: 12px;
            }
            margin: 0;
            padding: 0.5rem;
            width: 100%;
            font-size: 14px;
            color: #0000ff;
            text-align: center;
            opacity: 0.9;
            font-size: 16px !important;
        `,
    },
    image: {
        css: css`
            & img {
                width: auto;
            }
        `,
    },
};

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    return {
        pageLink: ownProps.fields.divisions.map(division => getLink(state, division.fields.destination)),
    };
};

const mapDispatchToProps = {};
type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const PdfList: React.FC<Props> = ({ fields, pageLink, extendedStyles }) => {
    const [styles] = useState(() => mergeToNew(pdfStyles, extendedStyles));
    const desktop = 5;
    const tablet = Math.ceil(desktop / 2);
    const mobile = Math.ceil(tablet / 2);
    const columnCount = 100 / fields.numberOfColumns;
    return (
        <StyledWrapper
            {...styles.divisionWrapper}
            data-test-selector="divisions"
            className={fields.isSpecialPage ? "specialPage" : ""}
        >
            <FlexWrapContainer {...styles.divisionContainer}>
                {fields.divisions.map((division, index) => (
                    <FlexItem
                        // eslint-disable-next-line react/no-array-index-key
                        key={index}
                        flexColumns={[mobile, tablet, tablet, desktop, desktop]}
                        {...styles.divisionItem}
                        css={css`
                            flex-basis: ${columnCount}% !important;
                            max-width: ${columnCount}% !important;
                            padding: 10px;
                            justify-content: center;
                            flex: 0 0 auto;
                            position: relative;
                            ${({ theme }: { theme: BaseTheme }) =>
                                breakpointMediaQueries(theme, [
                                    css`
                                        flex-basis: 100% !important;
                                        max-width: 100% !important;
                                    `,
                                    null,
                                    null,
                                    null,
                                    null,
                                ])}
                        `}
                    >
                        <StyledWrapper data-test-selector="divisionContent" className="divisionContent">
                            <Clickable href={pageLink[index]?.url} {...styles.link} target="_blank">
                                <LazyImage {...styles.image} src={division.fields.image} />
                                <Typography {...styles.divisionTitle}>
                                    {translate(division?.fields?.title)}
                                    </Typography>
                            </Clickable>
                        </StyledWrapper>
                    </FlexItem>
                ))}
            </FlexWrapContainer>
        </StyledWrapper>
    );
};

const contentTab = {
    displayName: "Content",
    sortOrder: 0,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(PdfList),
    definition: {
        group: "Common",
        icon: "Carousel",
        fieldDefinitions: [
            {
                name: fields.divisions,
                editorTemplate: "ListField",
                tab: contentTab,
                getDisplay: (item: HasFields) => {
                    return item.fields.title;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "title",
                        editorTemplate: "TextField",
                        defaultValue: "Slide",
                    },
                    {
                        name: "image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                    },
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                    },
                ],
            },
            {
                name: "numberOfColumns",
                editorTemplate: "DropDownField",
                defaultValue: 3,
                options: [{ value: 3 }, { value: 4 }, { value: 5 }],
                isRequired: true,
                fieldType: "General",
            },
            {
                name: "isSpecialPage",
                displayName: "Special page",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
